/**
 * Privacy Policy (P20250925-103)
 *
 * SEO-ready, indexable, with ToC and Last Updated stamp.
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function LegalPrivacy() {
  const lastUpdated = '2025-09-30';

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Attain Domains</title>
        <meta
          name="description"
          content="Attain Domains Privacy Policy: How we collect, use, and protect your data. Analytics are opt-in, no sale of personal info."
        />
        <link rel="canonical" href="https://attaindomains.com/legal/privacy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold mb-2 text-center">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Last updated: {lastUpdated}
          </p>

          {/* Table of Contents */}
          <nav className="bg-muted/30 rounded-lg p-6 mb-8" aria-label="Table of contents">
            <h2 className="text-lg font-semibold mb-3">Contents</h2>
            <ul className="space-y-1 text-sm">
              <li>
                <a href="#what-we-collect" className="text-primary hover:underline">
                  1. What We Collect
                </a>
              </li>
              <li>
                <a href="#how-we-use" className="text-primary hover:underline">
                  2. How We Use Your Information
                </a>
              </li>
              <li>
                <a href="#cookies" className="text-primary hover:underline">
                  3. Cookies & Tracking
                </a>
              </li>
              <li>
                <a href="#data-retention" className="text-primary hover:underline">
                  4. Data Retention
                </a>
              </li>
              <li>
                <a href="#your-rights" className="text-primary hover:underline">
                  5. Your Rights
                </a>
              </li>
              <li>
                <a href="#no-sale" className="text-primary hover:underline">
                  6. No Sale of Personal Info
                </a>
              </li>
              <li>
                <a href="#contact" className="text-primary hover:underline">
                  7. Contact Us
                </a>
              </li>
            </ul>
          </nav>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* What We Collect */}
            <section
              id="what-we-collect"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">1. What We Collect</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Minimal by design.</strong> We only collect what's necessary to operate
                  the service:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Account Data:</strong> Email (for magic-link auth and order
                    confirmations). No passwords stored.
                  </li>
                  <li>
                    <strong>Purchase Data:</strong> Domains purchased, total amount, Escrow
                    reference (for transaction tracking).
                  </li>
                  <li>
                    <strong>Contact Data:</strong> Name, email, message when you submit a support
                    request.
                  </li>
                  <li>
                    <strong>Analytics (opt-in):</strong> If you consent, we use PostHog (anonymous
                    event tracking), Clarity (session replays), and SalesIQ (live chat). All PII is
                    hashed before transmission.
                  </li>
                  <li>
                    <strong>Technical Data:</strong> IP address (rate-limiting only), browser type,
                    device info.
                  </li>
                </ul>
              </div>
            </section>

            {/* How We Use */}
            <section
              id="how-we-use"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                2. How We Use Your Information
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Process domain purchases and transfers via our trusted payment provider</li>
                <li>Send order confirmations and magic-link login emails (via Resend)</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Improve our website and services (analytics are opt-in)</li>
                <li>Comply with legal obligations (e.g., Escrow reporting)</li>
              </ul>
            </section>

            {/* Cookies */}
            <section
              id="cookies"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Cookies & Tracking</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Strictly necessary:</strong> Session cookie (HttpOnly, for login), cart
                  state (localStorage).
                </p>
                <p>
                  <strong>Analytics (opt-in):</strong> PostHog, Clarity, SalesIQ only load if you
                  click "Accept" in the cookie banner. You can revoke consent anytime via "Manage
                  Privacy" in the footer.
                </p>
                <p>
                  <strong>No third-party ad tracking.</strong> We don't use Google Analytics,
                  Facebook Pixel, or ad networks.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section
              id="data-retention"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Data Retention</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>
                  <strong>Orders:</strong> Retained indefinitely for tax/legal compliance.
                </li>
                <li>
                  <strong>Support requests:</strong> Retained for 2 years.
                </li>
                <li>
                  <strong>Analytics:</strong> PostHog retains anonymous events for 90 days. Clarity
                  session replays expire after 30 days.
                </li>
                <li>
                  <strong>Account data:</strong> Deleted upon request (see Your Rights below).
                </li>
              </ul>
            </section>

            {/* Your Rights */}
            <section
              id="your-rights"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Your Rights</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>You have the right to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Access:</strong> Request a copy of your data (email us at
                    privacy@attaindomains.com).
                  </li>
                  <li>
                    <strong>Correct:</strong> Update inaccurate data via your Account page.
                  </li>
                  <li>
                    <strong>Delete:</strong> Request account deletion (order history retained for
                    legal compliance).
                  </li>
                  <li>
                    <strong>Opt-out:</strong> Revoke analytics consent via "Manage Privacy" or email
                    us.
                  </li>
                  <li>
                    <strong>Data portability:</strong> Export your order history as CSV from your
                    Account page.
                  </li>
                </ul>
                <p>
                  <strong>Contact:</strong>{' '}
                  <Link to="/support/contact" className="text-primary hover:underline">
                    Submit a privacy request
                  </Link>{' '}
                  or email privacy@attaindomains.com.
                </p>
              </div>
            </section>

            {/* No Sale */}
            <section
              id="no-sale"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                6. No Sale of Personal Info
              </h2>
              <p className="text-muted-foreground">
                <strong>We do not sell, trade, or rent your personal information.</strong> We share
                data only with:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-3">
                <li>
                  <strong>our trusted payment provider:</strong> To facilitate secure domain
                  transfers (required by their terms).
                </li>
                <li>
                  <strong>Resend:</strong> To send transactional emails (order confirmations, magic
                  links).
                </li>
                <li>
                  <strong>Supabase:</strong> Our database provider (Lovable Cloud, encrypted at
                  rest).
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section
              id="contact"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Contact Us</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>Questions about this Privacy Policy or your personal data?</p>
                <p>
                  <strong>Email:</strong> privacy@attaindomains.com
                </p>
                <p>
                  <strong>Support:</strong>{' '}
                  <Link to="/support/contact" className="text-primary hover:underline">
                    Contact form
                  </Link>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
